/**
 * TablaGenerica — Generic editable table for IS engrase paso (REQ-9).
 *
 * Supports: add/remove rows, add/remove columns, edit column titles, edit cells.
 * Column ids are stable (crypto.randomUUID() at add time) so removing a column
 * never corrupts other columns' cell data (ADR-D1).
 *
 * ponytail: smallest shape that supports the 4 operations; reuses existing
 * table styling/primitives (border-collapse, border-border, overflow-x-auto).
 * ceiling = typed/numeric cells, column reorder, column width config.
 */
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/shared/ui/button.js";
import { cn } from "@/shared/lib/utils.js";
import type { TablaGenerica as TablaGenericaType } from "../../types.js";
import {
  addColumn,
  addRow,
  removeColumn,
  removeRow,
  renameColumn,
  setCell,
} from "./tabla-generica-helpers.js";

export function TablaGenerica(props: {
  tabla: TablaGenericaType;
  onChange: (next: TablaGenericaType) => void;
  disabled?: boolean;
}) {
  const { tabla, onChange, disabled } = props;

  function handleAddColumn() {
    const id = crypto.randomUUID();
    onChange(addColumn(tabla, id, ""));
  }

  function handleRemoveColumn(id: string) {
    onChange(removeColumn(tabla, id));
  }

  function handleRenameColumn(id: string, titulo: string) {
    onChange(renameColumn(tabla, id, titulo));
  }

  function handleAddRow() {
    onChange(addRow(tabla));
  }

  function handleRemoveRow(rowIndex: number) {
    onChange(removeRow(tabla, rowIndex));
  }

  function handleSetCell(rowIndex: number, columnId: string, value: string) {
    onChange(setCell(tabla, rowIndex, columnId, value));
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-md">
        {tabla.columnas.length === 0 && tabla.filas.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Sin columnas todavía. Agrega la primera con el botón de abajo.
          </p>
        ) : (
          <table className="w-full min-w-[28rem] border-collapse border border-border text-sm">
            <thead>
              <tr className="bg-secondary/60">
                {tabla.columnas.map((col) => (
                  <th
                    key={col.id}
                    className="border border-border px-2 py-1.5 text-left font-mono text-[0.625rem] uppercase tracking-[0.1em] text-muted-foreground"
                  >
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        value={col.titulo}
                        disabled={disabled}
                        placeholder="Título columna"
                        onChange={(e) => handleRenameColumn(col.id, e.target.value)}
                        aria-label={`Título de columna ${col.titulo || col.id}`}
                        className={cn(
                          "min-w-0 flex-1 rounded bg-transparent px-1 py-0.5 text-xs font-mono uppercase",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          "disabled:cursor-not-allowed disabled:opacity-50",
                        )}
                      />
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => handleRemoveColumn(col.id)}
                        aria-label={`Quitar columna ${col.titulo || col.id}`}
                        className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:text-destructive disabled:pointer-events-none disabled:opacity-50"
                      >
                        <Trash2 className="size-3" aria-hidden />
                      </button>
                    </div>
                  </th>
                ))}
                {tabla.columnas.length > 0 && (
                  <th className="w-10 border border-border" aria-label="Acciones fila" />
                )}
              </tr>
            </thead>
            <tbody>
              {tabla.filas.length === 0 && tabla.columnas.length > 0 ? (
                <tr>
                  <td
                    colSpan={tabla.columnas.length + 1}
                    className="border border-border px-3 py-3 text-center text-xs text-muted-foreground"
                  >
                    Sin filas. Agrega la primera con el botón de abajo.
                  </td>
                </tr>
              ) : (
                tabla.filas.map((fila, rowIndex) => (
                  <tr key={rowIndex} className="group">
                    {tabla.columnas.map((col) => (
                      <td key={col.id} className="border border-border p-1">
                        <input
                          type="text"
                          value={fila[col.id] ?? ""}
                          disabled={disabled}
                          onChange={(e) => handleSetCell(rowIndex, col.id, e.target.value)}
                          className={cn(
                            "w-full rounded bg-transparent px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                            "disabled:cursor-not-allowed disabled:opacity-50",
                          )}
                        />
                      </td>
                    ))}
                    {tabla.columnas.length > 0 && (
                      <td className="border border-border p-1 text-center">
                        <button
                          type="button"
                          disabled={disabled}
                          onClick={() => handleRemoveRow(rowIndex)}
                          aria-label={`Quitar fila ${rowIndex + 1}`}
                          className="rounded p-1 text-muted-foreground transition-colors hover:text-destructive disabled:pointer-events-none disabled:opacity-50"
                        >
                          <Trash2 className="size-3.5" aria-hidden />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={handleAddColumn}
          className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.12em]"
        >
          <Plus className="size-3.5" aria-hidden />
          Agregar columna
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={handleAddRow}
          className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.12em]"
        >
          <Plus className="size-3.5" aria-hidden />
          Agregar fila
        </Button>
      </div>
    </div>
  );
}
